import { useMemo } from 'react'

import {
  hasWorkspacePermission,
  type WorkspacePermissionCode
} from 'src/utils/workspacePermissions'

import { useWorkspacePermissionsContext } from './useWorkspacePermissionsContext'

/**
 * One boolean per requested code. Pass a single code for a single boolean, or
 * an array for an array of booleans in the same order.
 *
 * Unresolved permissions deny — use `useWorkspacePermissionsContext().isLoading`
 * when a caller must render a spinner instead of a denial.
 */
export const useHasWorkspacePermissions = <
  T extends WorkspacePermissionCode | WorkspacePermissionCode[]
>(
  input: T
): T extends WorkspacePermissionCode[] ? boolean[] : boolean => {
  const { permissions } = useWorkspacePermissionsContext()

  return useMemo(() => {
    if (Array.isArray(input)) {
      return input.map((code) => hasWorkspacePermission(permissions, code))
    }

    return hasWorkspacePermission(permissions, input)
  }, [permissions, input]) as T extends WorkspacePermissionCode[]
    ? boolean[]
    : boolean
}
